import { useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Reveal } from '../components/motion.tsx';
import { Avatar, Button, Callout, Dialog, Empty, Field, PageHead, Panel, Skeleton, StatusChip, useConfirm, useToast } from '../components/ui.tsx';
import { ApiError, get, post } from '../lib/api.ts';
import { date, idr } from '../lib/format.ts';
import { useLookups, useMe } from '../lib/session.ts';
import { GenerateGoodsButton } from './Goods.tsx';

export interface ThrRow {
  id: string;
  office: string;
  feast: string;
  year: number;
  transferDate: string;
  status: string;
  headcount: number;
  total: string;
}
interface ThrDetail extends Omit<ThrRow, 'headcount' | 'total'> {
  notes: string | null;
  createdBy: string | null;
  transferIssues: { employeeId: string; problem: string; detail: string }[];
  payments: { id: string; employeeId: string; name: string; employeeNo: string | null; basicSalary: string; totalEarnings: string; totalDeductions: string; received: string; workedMonths: number; isSecondCompany: boolean; split: { allowance: string; goodsPurchase: string } | null; lines: { name: string; kind: 'earning' | 'deduction'; amount: string; note: string | null }[] }[];
}

const err = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong.');

export function ThrRun() {
  const { id } = useParams({ strict: false }) as { id: string };
  const me = useMe().data!;
  const qc = useQueryClient();
  const toast = useToast();
  const { ask, node } = useConfirm();
  const q = useQuery({ queryKey: ['thr', id], queryFn: () => get<ThrDetail>(`/api/payroll/thr/${id}`) });
  const [open, setOpen] = useState<string | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['thr', id] });
    void qc.invalidateQueries({ queryKey: ['thr-runs'] });
  };
  const useAct = (path: string, ok: string, body?: Record<string, unknown>) =>
    useMutation({
      mutationFn: () => post<{ fileId?: string }>(`/api/payroll/thr/${id}/${path}`, body ?? {}),
      onSuccess: (r) => {
        toast({ title: ok });
        refresh();
        if (r?.fileId) window.location.assign(`/api/payroll/files/${r.fileId}`);
      },
      onError: (e) => toast({ title: err(e), tone: 'error' }),
    });
  const calculate = useAct('calculate', 'Recalculated');
  const approve = useAct('approve', 'Approved');
  const exportM = useAct('export', 'Bank files ready');
  const cancel = useMutation({ mutationFn: (reason: string) => post(`/api/payroll/thr/${id}/cancel`, { reason }), onSuccess: () => { toast({ title: 'Run cancelled' }); refresh(); }, onError: (e) => toast({ title: err(e), tone: 'error' }) });

  const r = q.data;
  if (!r) return <Skeleton h={420} />;
  const total = r.payments.reduce((n, p) => n + Number(p.received), 0);
  const second = r.payments.filter((p) => p.isSecondCompany);
  return (
    <div>
      <PageHead
        eyebrow={`THR · ${r.office} · ${r.year}`}
        title={
          <>
            {r.feast}, <em>paid {date(r.transferDate)}.</em>
          </>
        }
        lede="Basic salary plus THR-eligible components, prorated for anyone with less than twelve months’ service. Staff of second companies receive part as goods-purchase invoices, as before."
        aside={<StatusChip status={r.status} />}
      />
      <Reveal className="stack">
        <Reveal.Item className="grid-12">
          <div className="col-8">
            <div className="ledger" style={{ ['--cols' as string]: 3 }}>
              <div className="ledger__cell ledger__cell--accent">
                <div className="ledger__value">{idr(total.toFixed(2))}</div>
                <div className="ledger__label eyebrow">Total THR</div>
              </div>
              <div className="ledger__cell">
                <div className="ledger__value">{r.payments.length}</div>
                <div className="ledger__label eyebrow">People paid</div>
              </div>
              <div className="ledger__cell">
                <div className="ledger__value">{second.length}</div>
                <div className="ledger__label eyebrow">Second company</div>
              </div>
            </div>
          </div>
          <div className="col-4 stack" style={{ ['--gap' as string]: '8px', alignContent: 'start' }}>
            {me.access.payrollRun && (r.status === 'draft' || r.status === 'review') ? <Button icon="refresh" loading={calculate.isPending} onClick={() => calculate.mutate()}>Recalculate</Button> : null}
            {me.access.payrollApprove && r.status === 'review' ? (
              r.createdBy === me.userId ? (
                <p className="faint" style={{ fontSize: 'var(--step--1)' }}>You calculated this run, so a colleague approves it (four-eyes rule).</p>
              ) : (
                <Button variant="primary" loading={approve.isPending} onClick={async () => (await ask('Approve this THR run?', `${r.payments.length} payments, ${idr(total.toFixed(2))}.`, 'Approve')) && approve.mutate()}>Approve</Button>
              )
            ) : null}
            {me.access.payrollApprove && (r.status === 'approved' || r.status === 'paid') ? <Button variant="primary" icon="download" loading={exportM.isPending} disabled={!!r.transferIssues.length} onClick={() => exportM.mutate()}>Bank files</Button> : null}
            {me.access.goodsPurchase && second.length && (r.status === 'approved' || r.status === 'paid') ? <GenerateGoodsButton kind="thr" runId={r.id} /> : null}
            {me.access.payrollApprove && r.status !== 'paid' && r.status !== 'cancelled' ? <Button variant="ghost" onClick={async () => (await ask('Cancel this THR run?', 'Payments are kept for the record; a new run can be started for the same feast.', 'Cancel run', 'danger')) && cancel.mutate('Cancelled by payroll')}>Cancel run</Button> : null}
          </div>
        </Reveal.Item>
        {r.transferIssues.length ? (
          <Reveal.Item>
            <Callout tone="warn" title={`${r.transferIssues.length} transfer(s) need attention before bank files`}>
              {r.transferIssues.slice(0, 6).map((i) => `${r.payments.find((p) => p.employeeId === i.employeeId)?.name ?? i.employeeId}: ${i.detail}`).join(' · ')}
            </Callout>
          </Reveal.Item>
        ) : null}
        <Reveal.Item>
          <Panel eyebrow="Payments" title="Who receives what" flush>
            {r.payments.length ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th className="right">Months</th>
                    <th className="right">Basic</th>
                    <th className="right hide-sm">Components</th>
                    <th className="right">THR</th>
                  </tr>
                </thead>
                <tbody>
                  {r.payments.map((p) => (
                    <tr key={p.id} className="row-link" onClick={() => setOpen(p.id)}>
                      <td>
                        <div className="row" style={{ gap: 10 }}>
                          <Avatar name={p.name} size={28} />
                          <div>
                            <div>{p.name}</div>
                            <div className="faint mono" style={{ fontSize: 10.5 }}>{p.employeeNo}{p.isSecondCompany ? ' · second company' : ''}</div>
                          </div>
                        </div>
                      </td>
                      <td className="right num">{p.workedMonths}{p.workedMonths < 12 ? <span className="faint">/12</span> : null}</td>
                      <td className="right num nowrap">{idr(p.basicSalary)}</td>
                      <td className="right num faint nowrap hide-sm">
                        {Number(p.totalEarnings) ? `+${idr(p.totalEarnings)}` : '—'}
                        {Number(p.totalDeductions) ? <div>−{idr(p.totalDeductions)}</div> : null}
                      </td>
                      <td className="right num nowrap">{idr(p.received)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <Empty title="No payments yet.">Calculate the run to see who is paid.</Empty>
            )}
          </Panel>
        </Reveal.Item>
      </Reveal>
      {open ? <PaymentDialog p={r.payments.find((p) => p.id === open)!} onClose={() => setOpen(null)} /> : null}
      {node}
    </div>
  );
}

function PaymentDialog({ p, onClose }: { p: ThrDetail['payments'][number]; onClose: () => void }) {
  return (
    <Dialog open onClose={onClose} eyebrow={p.employeeNo ?? 'THR'} title={p.name} footer={<Button onClick={onClose}>Close</Button>}>
      <dl className="dl" style={{ gridTemplateColumns: '1fr auto' }}>
        <dt>Basic salary</dt>
        <dd className="num">{idr(p.basicSalary)}</dd>
        {p.lines.map((l, i) => (
          <div key={i} style={{ display: 'contents' }}>
            <dt>
              {l.name}
              {l.note ? <span className="faint"> · {l.note}</span> : null}
            </dt>
            <dd className="num" style={{ color: l.kind === 'deduction' ? 'var(--rose)' : undefined }}>{l.kind === 'deduction' ? '−' : '+'}{idr(l.amount)}</dd>
          </div>
        ))}
        <dt><b>THR</b></dt>
        <dd className="num"><b>{idr(p.received)}</b></dd>
        {p.split ? (
          <>
            <dt className="faint">Transferred as allowance</dt>
            <dd className="num faint">{idr(p.split.allowance)}</dd>
            <dt className="faint">As goods purchase</dt>
            <dd className="num faint">{idr(p.split.goodsPurchase)}</dd>
          </>
        ) : null}
      </dl>
      <p className="faint" style={{ fontSize: 'var(--step--1)', marginTop: 12 }}>{p.workedMonths} of 12 months counted.</p>
    </Dialog>
  );
}

export function NewThrDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const lookups = useLookups();
  const me = useMe().data!;
  const navigate = useNavigate();
  const toast = useToast();
  const [f, setF] = useState({ officeId: me.employee?.officeId ?? '', feastDayId: '', transferDate: '' });
  const create = useMutation({
    mutationFn: async () => {
      const run = await post<{ id: string }>('/api/payroll/thr', { officeId: f.officeId || lookups.data?.offices[0]?.id, feastDayId: f.feastDayId || lookups.data?.feastDays[0]?.id, transferDate: f.transferDate });
      await post(`/api/payroll/thr/${run.id}/calculate`);
      return run;
    },
    onSuccess: (run) => {
      toast({ title: 'THR calculated', body: 'Review it, then ask a colleague to approve.' });
      onClose();
      navigate({ to: '/payroll/thr/$id', params: { id: run.id } });
    },
  });
  return (
    <Dialog open={open} onClose={onClose} eyebrow="Holiday allowance" title={<>Which <em className="serif-italic">feast?</em></>} footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={create.isPending} disabled={!f.transferDate} onClick={() => create.mutate()}>Create and calculate</Button></>}>
      <div className="stack">
        <Field label="Office">{(p) => <select {...p} className="select" value={f.officeId} onChange={(e) => setF({ ...f, officeId: e.target.value })}>{lookups.data?.offices.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>}</Field>
        <Field label="Feast day" hint="Staff of the feast’s religion(s) are included">{(p) => <select {...p} className="select" value={f.feastDayId} onChange={(e) => setF({ ...f, feastDayId: e.target.value })}>{lookups.data?.feastDays.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>}</Field>
        <Field label="Transfer date" hint="Months of service are counted up to this date (at least 7 days before the feast, by law)">{(p) => <input {...p} type="date" className="input" value={f.transferDate} onChange={(e) => setF({ ...f, transferDate: e.target.value })} />}</Field>
        {create.error ? <Callout tone="bad">{err(create.error)}</Callout> : null}
      </div>
    </Dialog>
  );
}
